import { useState, useEffect } from "react";
import {
  GraduationCap,
  Users,
  Calendar,
  Award,
  BookOpen,
  TrendingUp,
  Clock,
} from "lucide-react";
import OrganicShape from "../Board/OrganicShape";
import { fetchBoardTraining } from "../../services/businessDashApi";
import {
  normalizeBoardTraining,
  mapModuleOrCourseRow,
  mapCompetencyRow,
} from "../../lib/normalizeBoardTraining";

const BoardTrainingDevelopment = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [model, setModel] = useState(() => normalizeBoardTraining(null));

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const raw = await fetchBoardTraining();
        if (!alive) return;
        setModel(normalizeBoardTraining(raw));
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.status === 404 ? "not_found" : "failed");
        setModel(normalizeBoardTraining(null));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const modules = model.modules.map(mapModuleOrCourseRow);
  const courses = model.courses.map(mapModuleOrCourseRow);
  const competencies = model.competencies.map(mapCompetencyRow);
  const s = model.stats;

  const fmt = (v) => (v != null && v !== "" ? v : "—");

  const getTypeColor = (type) => {
    switch (type) {
      case "Workshop":
        return "bg-blue-500/10 text-blue-600";
      case "Seminar":
        return "bg-amber-500/10 text-amber-600";
      case "Certification":
        return "bg-emerald-500/10 text-emerald-600";
      default:
        return "bg-primary/10 text-primary";
    }
  };

  const empty =
    !loading &&
    !error &&
    modules.length === 0 &&
    courses.length === 0 &&
    competencies.length === 0;

  return (
    <div className="space-y-8">
      {loading && (
        <p className="text-xs text-muted-foreground rounded-md border border-border/50 bg-muted/10 px-3 py-2">
          Loading training & development…
        </p>
      )}
      {!loading && error === "not_found" && (
        <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2">
          Training placeholder endpoint not available (
          <code className="text-[10px]">GET /api/admin-dash/board-training/</code>).
        </p>
      )}
      {!loading && error === "failed" && (
        <p className="text-xs text-destructive/90 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2">
          Could not load training data. Check your connection or try again later.
        </p>
      )}
      {!loading && empty && !error && (
        <p className="text-xs text-muted-foreground rounded-md border border-border/50 bg-muted/10 px-3 py-2">
          No training modules or courses returned yet.
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <OrganicShape className="p-5 bg-gradient-subtle text-center">
          <BookOpen className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{fmt(s.completed_modules)}</p>
          <p className="text-xs text-foreground/60">Completed modules</p>
        </OrganicShape>
        <OrganicShape className="p-5 bg-gradient-subtle text-center">
          <GraduationCap className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{fmt(s.total_modules)}</p>
          <p className="text-xs text-foreground/60">Total modules</p>
        </OrganicShape>
        <OrganicShape className="p-5 bg-gradient-subtle text-center">
          <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{fmt(s.hours_logged)}</p>
          <p className="text-xs text-foreground/60">Hours logged</p>
        </OrganicShape>
        <OrganicShape className="p-5 bg-gradient-subtle text-center">
          <Award className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{fmt(s.certificates_earned)}</p>
          <p className="text-xs text-foreground/60">Certificates earned</p>
        </OrganicShape>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <OrganicShape className="p-6 bg-card/50">
          <div className="flex items-center gap-3 mb-5">
            <BookOpen className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Modules</h3>
          </div>
          <div className="space-y-4">
            {modules.length === 0 && !loading && (
              <p className="text-sm text-muted-foreground">No modules listed.</p>
            )}
            {modules.map((row) => (
              <div
                key={row.id}
                className="p-4 bg-background/50 rounded-lg border border-border/50"
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h4 className="font-medium text-foreground text-sm">{row.title}</h4>
                  <span
                    className={`text-xs px-2 py-1 rounded-full shrink-0 ${getTypeColor(row.type)}`}
                  >
                    {row.type}
                  </span>
                </div>
                {row.description ? (
                  <p className="text-xs text-muted-foreground mb-2">{row.description}</p>
                ) : null}
                <div className="flex items-center gap-4 text-xs text-foreground/60 mb-2 flex-wrap">
                  {row.date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(row.date).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  )}
                  {row.provider ? <span>{row.provider}</span> : null}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-foreground/40" />
                  <span className="text-xs text-foreground/60">
                    {row.participants.length} participants
                  </span>
                </div>
              </div>
            ))}
          </div>
        </OrganicShape>

        <div className="space-y-6">
          <OrganicShape className="p-6 bg-card/50">
            <div className="flex items-center gap-3 mb-5">
              <Calendar className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">Courses</h3>
            </div>
            <div className="space-y-3">
              {courses.length === 0 && !loading && (
                <p className="text-sm text-muted-foreground">No courses in catalog.</p>
              )}
              {courses.map((row, index) => (
                <div
                  key={row.id ?? index}
                  className="p-3 bg-primary/5 rounded-lg border border-primary/10"
                >
                  <h4 className="font-medium text-foreground text-sm mb-1">{row.title}</h4>
                  <div className="flex items-center justify-between text-xs text-foreground/60 gap-2">
                    {row.date && (
                      <span>
                        {new Date(row.date).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full shrink-0 ${getTypeColor(row.type)}`}>
                      {row.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </OrganicShape>

          {competencies.length > 0 && (
            <OrganicShape className="p-6 bg-card/50">
              <div className="flex items-center gap-3 mb-5">
                <Award className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Competencies</h3>
              </div>
              <div className="space-y-3">
                {competencies.map((comp) => (
                  <div key={comp.area} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-foreground/80">{comp.area}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${Math.min(100, (comp.level / 5) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground w-8">{comp.level}</span>
                      {comp.trend === "up" && (
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </OrganicShape>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardTrainingDevelopment;
